// Booking move, change-request, pay-link and re-confirmation contract
// (epic #2691). Every wire shape is an alias of the generated backend schema
// (ADR-005); this module adds only UI-side helpers: the price-choice union,
// the error codes the move routes return, and two small utilities.

import type { Schema } from "../../types/generated";

// ---- Shared shapes ---------------------------------------------------------

export type PriceDifference = Schema["PriceDifference"];
export type PriceDifferenceDirection = PriceDifference["direction"];
/** A court + interval; the move quote, request and alternatives all use it. */
export type MoveSlot = Schema["BookingSlot"];

// ---- Staff move ------------------------------------------------------------

export type BookingMoveQuoteBody = Schema["BookingMoveQuoteRequest"];
export type BookingMoveQuote = Schema["BookingMoveQuoteResponse"];
export type MoveRepricing = Schema["MoveRepricing"];
export type BookingMoveBody = Schema["BookingMoveRequest"];
export type BookingMoveResponse = Schema["BookingMoveResponse"];
export type MovePricePolicy = BookingMoveBody["price_policy"];
export type MoveDecreaseTo = NonNullable<BookingMoveBody["decrease_to"]>;

/**
 * The price part of a staff move body, as the move sheet chooses it. Payments
 * are online only: an adjusted increase is always collected by a pay link
 * (no extra field), a decrease names where the refund goes.
 */
export type MovePriceChoice =
  | { price_policy: "keep" }
  | { price_policy: "adjust"; decrease_to: MoveDecreaseTo }
  | { price_policy: "adjust" };

// ---- Pay links and move refunds (staff reads and actions) -------------------

/** GET /facilities/{fid}/bookings/{bid}/pay-links; resend and waive return one. */
export type PayLink = Schema["PayLinkView"];
export type PayLinkStatus = PayLink["status"];
/** GET /facilities/{fid}/bookings/{bid}/move-refunds. */
export type MoveRefund = Schema["MoveRefundView"];

// ---- Player change requests ------------------------------------------------

export type ChangeRequestState = Schema["ChangeRequestResponse"]["state"];
export type ChangeRequestCreate = Schema["ChangeRequestCreate"];
export type ChangeRequestResponse = Schema["ChangeRequestResponse"];
/** One row of the staff queue, GET /facilities/{fid}/change-requests. */
export type ChangeRequestQueueItem = Schema["ChangeRequestListItem"];
export type ChangeRequestActionBody = Schema["ChangeRequestDecision"];
/** One row of the owner's own requests, GET /bookings/{bid}/change-requests. */
export type OwnChangeRequest = Schema["PlayerChangeRequestItem"];

// ---- Re-confirmation and policy -------------------------------------------

export type ReconfirmBody = Schema["ReconfirmRequest"];
export type ReconfirmResponse = Schema["ReconfirmResponse"];
export type ReconfirmStatus = ReconfirmResponse["participant_status"];
export type ReschedulePolicy = Schema["ReschedulePolicyResponse"];

// ---- Staff bookings ---------------------------------------------------------

export type StaffBookingPayment = Schema["StaffBookingCreate"]["payment"];

// ---- Errors -----------------------------------------------------------------

/**
 * Error codes the scheduling routes return. Errors use the FastAPI shape
 * `{"detail": {"code", "message", "alternatives"?}}`; the shared http client
 * surfaces the detail object as `ApiError.details`.
 */
export const MOVE_ERROR = {
  slotUnavailable: "slot_unavailable", // 409, with alternatives
  bookingChanged: "booking_changed", // 409, concurrent move or edit
  moveNotAllowed: "move_not_allowed", // 422, started/ended/not confirmed
  insideCutoff: "inside_reschedule_cutoff", // 422, with cutoff_hours
  requestExpired: "request_expired", // 409
  requestNotPending: "request_not_pending", // 409
  walletUnavailable: "wallet_unavailable", // 422: no club wallet to credit yet
  refundUnavailable: "refund_unavailable", // 422: booking paid offline
  refundPending: "refund_pending", // 409: a move refund is still in flight
  paymentPending: "payment_pending", // 409: resend or waive the open pay link first
  reconfirmClosed: "reconfirm_closed", // 409: started, checked in, superseded or answered
  waiveRequiresStaffRole: "waive_requires_staff_role", // 403
  fullRefundRequiresOverride: "full_refund_requires_override", // 403
  priceChangeRequiresSettlement: "price_change_requires_settlement", // 422
  extraNotAuthorised: "extra_not_authorised", // 422: player must authorise the extra
  cardOnFileUnavailable: "card_on_file_unavailable", // 422: no usable saved card
  courtChangeNotAllowed: "court_change_not_allowed", // 422: players can't change the court
} as const;

/** Reads `alternatives` from a slot-unavailable error's details, if present. */
export function alternativesFromErrorDetails(
  details: Record<string, unknown> | undefined,
): MoveSlot[] {
  const raw = details?.alternatives;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is MoveSlot =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as MoveSlot).start_time === "string" &&
      typeof (item as MoveSlot).end_time === "string" &&
      typeof (item as MoveSlot).court_id === "string",
  );
}

/** A fresh idempotency key for one user action. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
